import React from 'react';

interface CitySkylineSvgProps {
  className?: string;
}

/**
 * Lightweight decorative city skyline, drawn as inline SVG rather than a
 * stack of CSS divs. Uses `currentColor` for fills so the illustration
 * inherits whatever text color the parent sets (e.g. hero's white-on-gradient
 * text), keeping it themeable without hard-coded hex values.
 */
const CitySkylineSvg: React.FC<CitySkylineSvgProps> = ({ className }) => {
  return (
    <svg
      viewBox="0 0 1200 220"
      preserveAspectRatio="none"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      {/* Back row — shorter, more transparent buildings for depth */}
      <g fill="currentColor" opacity={0.35}>
        <rect x="0" y="120" width="60" height="100" />
        <rect x="70" y="90" width="50" height="130" />
        <rect x="130" y="140" width="70" height="80" />
        <rect x="210" y="100" width="55" height="120" />
        <rect x="275" y="130" width="65" height="90" />
        <rect x="350" y="80" width="50" height="140" />
        <rect x="410" y="115" width="60" height="105" />
        <rect x="480" y="95" width="55" height="125" />
        <rect x="545" y="135" width="70" height="85" />
        <rect x="625" y="100" width="60" height="120" />
        <rect x="695" y="125" width="55" height="95" />
        <rect x="760" y="85" width="50" height="135" />
        <rect x="820" y="110" width="65" height="110" />
        <rect x="895" y="140" width="60" height="80" />
        <rect x="965" y="95" width="55" height="125" />
        <rect x="1030" y="120" width="70" height="100" />
        <rect x="1110" y="90" width="60" height="130" />
      </g>

      {/* Front row — taller, fully opaque silhouette with simple window rhythm */}
      <g fill="currentColor" opacity={0.85}>
        <rect x="10" y="60" width="46" height="160" />
        <rect x="65" y="100" width="38" height="120" />
        <rect x="112" y="40" width="52" height="180" />
        <rect x="175" y="80" width="40" height="140" />
        <rect x="225" y="20" width="58" height="200" />
        <rect x="295" y="70" width="42" height="150" />
        <rect x="348" y="110" width="46" height="110" />
        <rect x="405" y="50" width="50" height="170" />
        <rect x="465" y="90" width="38" height="130" />
        <rect x="515" y="30" width="60" height="190" />
        <rect x="590" y="75" width="44" height="145" />
        <rect x="645" y="115" width="40" height="105" />
        <rect x="695" y="55" width="52" height="165" />
        <rect x="760" y="95" width="42" height="125" />
        <rect x="815" y="35" width="56" height="185" />
        <rect x="885" y="80" width="40" height="140" />
        <rect x="935" y="45" width="50" height="175" />
        <rect x="1000" y="105" width="44" height="115" />
        <rect x="1055" y="65" width="52" height="155" />
        <rect x="1120" y="100" width="46" height="120" />
      </g>

      {/* Windows — sparse lit squares scattered across the front row */}
      <g fill="currentColor" opacity={0.5}>
        {Array.from({ length: 26 }).map((_, i) => {
          const x = 24 + i * 46;
          const y = 70 + ((i * 37) % 90);
          return <rect key={i} x={x} y={y} width="6" height="6" />;
        })}
      </g>
    </svg>
  );
};

export default CitySkylineSvg;
